import { delay, race, select, take } from 'redux-saga/effects';

import { OperationStatus } from '@pressreader/appstore';

import { loadHotspotInfoCancelled, loadHotspotInfoDone, loadHotspotInfoFailed, sponsorshipStateActions } from './actions';
import { getHotspotInfoLoadingStatus, getSponsor } from './selectors';

// Cap on how long callers will wait for a terminal sponsorship action when state is unresolved.
// On timeout we proceed with whatever state is current rather than blocking analytics forever.
const ACTIVE_DETECTION_WAIT_MS = 10_000;

/**
 * Block until the sponsorship state is in a known shape. Resolution is determined from
 * (`loading`, `sponsor`) together: `sponsor.status === undefined` is ambiguous between
 * "never loaded" and "loaded, no session" so it cannot drive the gate alone.
 *
 * Observe-only: the gate does NOT dispatch `loadHotspotInfoAction` itself — bootstrap owns
 * sponsor detection. `userChangeDone` → `onUserChangeDone` dispatches `loadHotspotInfoAction`
 * (unblocks via `loadHotspotInfoDone`/`Failed`/`Cancelled`); `initSponsorship` dispatches
 * `sponsorshipChanged` directly (unblocks via that). Dispatching from here would cause a
 * fan-out via `sponsorshipChanged` subscribers (catalog reload, etc.) and we observed reload
 * cycles when the gate doubled as a producer. We just wait briefly on a terminal action.
 *
 * Wait policy:
 *  - If `loading` is `Done`/`Failed` or `sponsor` is already populated → return immediately.
 *  - Otherwise wait up to `timeoutMs` (default {@link ACTIVE_DETECTION_WAIT_MS}) for the next
 *    terminal action.
 *  - On timeout, return with whatever state we have rather than blocking forever — analytics
 *    must not stall on a wedged backend.
 *
 * Also used to gate the websdk's first paint on the boot sponsor state (the embedded-mode flag
 * decides whether above-the-fold auth chrome renders); that caller passes a shorter `timeoutMs`
 * so a slow/failed sponsor endpoint degrades to showing the UI rather than a blank embed.
 */
function* ensureHotspotInfoLoaded(timeoutMs: number = ACTIVE_DETECTION_WAIT_MS) {
    const loading: ReturnType<typeof getHotspotInfoLoadingStatus> = yield select(getHotspotInfoLoadingStatus);
    const sponsor: ReturnType<typeof getSponsor> = yield select(getSponsor);
    if (loading === OperationStatus.Done || loading === OperationStatus.Failed || sponsor) {
        return;
    }

    yield race({
        terminal: take([loadHotspotInfoDone, loadHotspotInfoFailed, loadHotspotInfoCancelled, ...sponsorshipStateActions]),
        timeout: delay(timeoutMs),
    });
}

export { ensureHotspotInfoLoaded };
